import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_LANGUAGES, getLanguageObject } from '../constants/app-languages';
import { loadLanguage } from '../i18n';

const LanguageContext = createContext(null);

export function LanguageProvider({ children, language }) {
  const { i18n, t } = useTranslation();
  const localStorageLanguage = sessionStorage.getItem('@contenta-language');
  const [languageObject, setLanguageObject] = useState(
    APP_LANGUAGES.find((idiom) => idiom.key === i18n?.language)
  );
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorageLanguage || language || 'en-US'
  );

  // index.html ships lang="pt-br"; without this the document keeps claiming
  // Portuguese to crawlers and screen readers after the user switches. Keyed on
  // i18n.language because both switch paths below go through changeLanguage,
  // while only one of them updates currentLanguage.
  useEffect(() => {
    if (i18n?.language) document.documentElement.lang = i18n.language;
  }, [i18n?.language]);

  // Menus have handed these a dropdown key instead of a language. Switching to
  // one would store the literal string 'undefined' and ask i18next for a bundle
  // that cannot exist, so an unknown language is a no-op.
  const isSupported = (idiom) =>
    APP_LANGUAGES.some((language) => language.key === idiom);

  async function handleChangeLanguage(idiom) {
    if (!isSupported(idiom)) return;

    await loadLanguage(idiom);
    setCurrentLanguage(idiom);
    i18n.changeLanguage(idiom);
    sessionStorage.setItem('@contenta-language', idiom);
    setLanguageObject(APP_LANGUAGES.find((i) => i.key === idiom));
  }

  const onLanguageMenuClick = async ({ key }) => {
    if (!isSupported(key)) return;

    await loadLanguage(key);
    i18n.changeLanguage(key);
    sessionStorage.setItem('@contenta-language', key);
    setLanguageObject(APP_LANGUAGES.find((idiom) => idiom.key === key));
  };

  const languagesOptions = getLanguageObject(t).sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  const value = useMemo(
    () => ({
      languagesOptions,
      currentLanguage,
      setCurrentLanguage,
      onLanguageMenuClick,
      handleChangeLanguage,
      languageObject,
      // the context holds the BCP47 tag ('pt-BR'); comparing against 'PT-BR'
      // (the APP_LANGUAGES `value`) silently never matched
      isPortuguese: (currentLanguage || '').toLowerCase() === 'pt-br',
      faqLanguage: languageObject?.value || 'EN',
      // Stripe has no 'en-US' locale, and rejects what it does not know. It is
      // read from i18n.language, not currentLanguage, because the flag menu
      // changes only the former.
      stripeLocale:
        (i18n?.language || '').toLowerCase() === 'pt-br' ? 'pt-BR' : 'en',
    }),
    [currentLanguage, languageObject, languagesOptions, i18n?.language]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => React.useContext(LanguageContext);
